import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2 } from "lucide-react";
import { Autocomplete } from "@/components/ui/autocomplete";
import { Button } from "@akuna/ui";
import { cn } from "@/lib/utils";
import type { VaccineOption } from "@/hooks/useApplications";

interface ApplicationAutocompleteProps {
  options: VaccineOption[];
  isLoading?: boolean;
  disabled?: boolean;
  value?: string;
  selectedValue?: string;
  onChange: (value: string) => void;
  onSelectOption: (option: VaccineOption, displayName: string) => void;
  onOpenCreateDialog?: (name: string) => void;
  onCreateNew?: (name: string) => void;
  isCreating?: boolean;
  placeholder?: string;
  popoverPortalled?: boolean;
}

const formatName = (name?: string | null) => name?.trim() ?? "";

export const ApplicationAutocomplete = ({
  options,
  isLoading = false,
  disabled = false,
  value,
  selectedValue,
  onChange,
  onSelectOption,
  onOpenCreateDialog,
  onCreateNew,
  isCreating = false,
  placeholder,
  popoverPortalled = true,
}: ApplicationAutocompleteProps) => {
  const { t } = useTranslation("reports");
  // Garantir que value seja sempre uma string
  const safeValue = value ?? "";
  const resolvedPlaceholder = placeholder ?? t("vaccines.autocomplete.placeholder");

  const sortedOptions = useMemo(
    () =>
      [...options]
        .filter((o) => o.label || o.rawName)
        .sort((a, b) => {
          const nameA = a.label || a.rawName || "";
          const nameB = b.label || b.rawName || "";
          return nameA.localeCompare(nameB, "pt-BR", { sensitivity: "base" });
        }),
    [options],
  );

  const autocompleteOptions = useMemo(
    () =>
      sortedOptions.map((option) => ({
        label: option.isDeleted
          ? `${formatName(option.label || option.rawName)} (${t("vaccines.autocomplete.deleted")})`
          : formatName(option.label || option.rawName),
        value: option.value,
      })),
    [sortedOptions, t],
  );

  const optionsByValue = useMemo(
    () => new Map(sortedOptions.map((option) => [option.value, option])),
    [sortedOptions],
  );

  const emptyMessage = isLoading ? (
    <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      {t("vaccines.autocomplete.loading")}
    </div>
  ) : (
    <div className="px-3 py-4 text-sm text-muted-foreground">
      <p>{t("vaccines.autocomplete.empty")}</p>
    </div>
  );

  const normalizedValue = safeValue.trim();
  const createButtonLabel = normalizedValue
    ? `${t("vaccines.autocomplete.createNew")}: "${safeValue}"`
    : t("vaccines.autocomplete.createNew");

  const footer = onOpenCreateDialog ? (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="w-full justify-start text-primary hover:bg-accent"
      onClick={(e) => {
        e.stopPropagation();
        onOpenCreateDialog(safeValue);
      }}
    >
      {createButtonLabel}
    </Button>
  ) : null;

  const createFooter = onCreateNew ? (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={isCreating}
      className="w-full justify-start text-primary hover:bg-accent"
      onClick={(e) => {
        e.stopPropagation();
        onCreateNew(safeValue);
      }}
    >
      {isCreating ? (
        <span className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("vaccines.autocomplete.creating")}
        </span>
      ) : (
        <>{createButtonLabel}</>
      )}
    </Button>
  ) : null;

  return (
    <Autocomplete
      disabled={disabled}
      options={autocompleteOptions}
      value={safeValue}
      onChange={onChange}
      onSelect={(option) => {
        const selected = optionsByValue.get(option.value);
        if (!selected) return;
        onSelectOption(selected, option.label);
      }}
      selectedValue={selectedValue ?? ""}
      placeholder={resolvedPlaceholder}
      emptyMessage={emptyMessage}
      footer={createFooter ?? footer}
      popoverPortalled={popoverPortalled}
      openOnFocus={false}
      openOnClick
      renderOption={(option, isSelected) => {
        const selected = optionsByValue.get(option.value);
        if (!selected) return option.label;

        return (
          <>
            <Check
              className={cn(
                "mr-2 h-4 w-4",
                isSelected ? "opacity-100" : "opacity-0",
              )}
            />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium leading-none">{formatName(selected.label || selected.rawName)}</p>
                {selected.isDeleted ? (
                  <span className="rounded-full border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-[11px] font-medium leading-none text-destructive">
                    {t("vaccines.autocomplete.deletedItem")}
                  </span>
                ) : null}
              </div>
              {selected.applicationType ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  {selected.applicationType === "medicamento" ? t("vaccines.filters.medicationType") : t("vaccines.filters.vaccineType")}
                </p>
              ) : null}
              {selected.isDeleted ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("vaccines.autocomplete.deletedItemDescription")}
                </p>
              ) : null}
            </div>
          </>
        );
      }}
    />
  );
};

export const VaccineAutocomplete = ApplicationAutocomplete;
